import isFunction from 'lodash/isFunction.js';
import get from 'lodash/get.js';
import SortControls from './sort-controls.vue.js';
import Checkbox from './cell-types/pendo-checkbox-cell.vue.js';
import Tree from './cell-types/tree.vue.js';
import PendoIcon from '../icon/pendo-icon.vue.js';
import FilterPanel from './filter-panel.vue.js';
import PendoTooltip from '../../directives/tooltip/pendo-tooltip.js';
import { findNodeByContent, elementHasOverflow } from '../../utils/dom.js';

PendoTooltip.options.disposeTimeout = 0;
var TableHeaderCell = {
  name: 'TableHeaderCell',
  inject: ['$table'],
  components: {
    SortControls,
    Checkbox,
    Tree,
    FilterPanel,
    PendoIcon
  },
  directives: {
    PendoTooltip
  },
  props: {
    column: {
      type: Object
    },
    columnIndex: {
      type: Number
    }
  },
  data() {
    return {
      tooltipContent: null
    };
  },
  computed: {
    fixedColumnStyle() {
      if (this.$table.overflowX) {
        if (this.column.fixed === 'left') {
          return {
            left: `${this.column.fixedPos}px`
          };
        }
        if (this.column.fixed === 'right') {
          return {
            right: `${this.column.fixedPos}px`
          };
        }
      }
    },
    headerCellClassName() {
      if (this.$table.headerCellClassName) {
        return isFunction(this.$table.headerCellClassName) ? this.$table.headerCellClassName({
          row: {},
          column: this.column
        }) : this.$table.headerCellClassName;
      }
      return '';
    },
    isFiltered() {
      if (this.column.filters) {
        return this.column.filters.some(col => col.checked);
      }
      return false;
    },
    isFixedRight() {
      return this.column.fixed === 'right' && this.$table.overflowX && this.$table.scrollRightToLeft;
    },
    isFixedLeft() {
      return this.column.fixed === 'left' && this.$table.overflowX && this.$table.scrollLeftToRight;
    },
    isSorted() {
      const isColumnSorted = get(this.$table.sortStore, 'column.property') === this.column.property;
      const isAscDescSortActive = ['ascending', 'descending'].includes(this.$table.sortStore.activeSort);
      return isColumnSorted && isAscDescSortActive;
    },
    isHover() {
      return this.columnIndex === this.$table.hoverColumnHeader || this.$table.filterStore.column === this.column && this.$table.filterStore.visible;
    },
    showEllipsis() {
      return (this.column.showOverflow || this.$table.showAllOverflow) === 'ellipsis';
    },
    showTitle() {
      return (this.column.showOverflow || this.$table.showAllOverflow) === 'title';
    },
    showTooltip() {
      if (['actions', 'index', 'checkbox', 'tree'].includes(this.column.type)) {
        return false;
      }
      return this.column.showOverflow === true || this.column.showOverflow === 'tooltip' || this.$table.showAllOverflow === true || this.$table.showAllOverflow === 'tooltip';
    }
  },
  methods: {
    handleCellMouseenter(_ref) {
      let {
        currentTarget
      } = _ref;
      this.$table.toggleColumnHeaderHover(this.column, this.columnIndex);
      const cellContentNode = findNodeByContent(this.column.label, currentTarget);
      if (!cellContentNode.length) {
        this.tooltipContent = null;
        return;
      }
      if (elementHasOverflow(cellContentNode[0])) {
        requestAnimationFrame(() => {
          this.tooltipContent = this.column.label;
        });
      } else {
        this.tooltipContent = null;
      }
    },
    handleCellMouseleave() {
      this.$table.toggleColumnHeaderHover();
    },
    handleResizeMousedown($event) {
      this.$emit('resize', $event, this.column, this.columnIndex);
    },
    onCheckboxChange($event) {
      this.$table.toggleAllRowsSelect($event);
    },
    toggleSort($event) {
      $event.stopPropagation();
      if (this.column.sortable) {
        return this.$table.setActiveSort(this.column.property);
      }
    }
  },
  render(createElement) {
    const {
      $table,
      column,
      columnIndex,
      isFiltered,
      isFixedRight,
      isFixedLeft,
      isHover,
      isSorted,
      fixedColumnStyle,
      handleResizeMousedown,
      headerCellClassName,
      onCheckboxChange,
      showTooltip,
      showEllipsis,
      tooltipContent
    } = this;
    const {
      colSpan,
      filters,
      label,
      property,
      rowSpan,
      sortable,
      type
    } = column;
    function renderChildren() {
      let children = label;
      const slotName = `${type || property}Header`;
      if ($table.$scopedSlots && $table.$scopedSlots[slotName]) {
        children = $table.$scopedSlots[slotName]({
          column,
          columnIndex
        });
      }
      if (type) {
        switch (type) {
          case 'checkbox':
            if ($table.checkboxConfig.headerLabel) {
              children = $table.checkboxConfig.headerLabel;
            } else if ($table.checkboxConfig.selectAll !== false) {
              children = createElement(Checkbox, {
                props: {
                  checked: $table.isAllSelected,
                  indeterminate: $table.isIndeterminate
                },
                on: {
                  change: onCheckboxChange
                }
              });
            }
            break;
          case 'tree':
            if ($table.treeConfig.expandAll !== false) {
              children = createElement(Tree);
            }
            break;
          case 'index':
            children = label || '#';
            break;
          default:
            children = label;
            break;
        }
      }
      return createElement('span', [children]);
    }
    function renderActions() {
      const children = [];
      if (sortable) {
        children.push(createElement(SortControls, {
          props: {
            column
          }
        }));
      }
      if (filters && filters.length) {
        children.push(createElement(FilterPanel, {
          props: {
            column
          }
        }));
      }
      if (children.length) {
        return createElement('div', {
          class: 'pendo-table__column-actions'
        }, children);
      }
      return null;
    }
    function renderResizeControl() {
      if ($table.resizable && column.allowResize) {
        return createElement('div', {
          class: 'pendo-table__resizable',
          on: {
            mousedown: handleResizeMousedown
          }
        });
      }
      return null;
    }
    return createElement('th', {
      class: ['pendo-table__column', headerCellClassName, {
        'pendo-table__column--sortable': column.sortable,
        'pendo-table__column--filterable': column.filters && column.filters.length,
        'pendo-table__column--center': column.headerAlign === 'center',
        'pendo-table__column--right': column.headerAlign === 'right',
        'pendo-table__column--fixed-left': column.fixed === 'left' && $table.overflowX,
        'pendo-table__column--fixed-right': column.fixed === 'right' && $table.overflowX,
        'pendo-table__column--fixed-shadow': column.fixedShadow,
        'is-hover': isHover,
        'is-fixed-left': isFixedLeft,
        'is-fixed-right': isFixedRight,
        'is-filtered': isFiltered,
        'is-sorted': isSorted
      }],
      attrs: {
        'aria-sort': isSorted && $table.sortStore.activeSort,
        'aria-hidden': column.type === 'actions',
        'aria-rowindex': 1,
        'aria-colindex': columnIndex + 1,
        'role': 'columnheader',
        'scope': 'col',
        'rowspan': rowSpan,
        'colspan': colSpan
      },
      style: fixedColumnStyle,
      on: {
        mouseenter: this.handleCellMouseenter,
        mouseleave: this.handleCellMouseleave,
        click: this.toggleSort
      }
    }, [createElement('div', {
      class: ['pendo-table__cell', {
        'pendo-table__cell--index': column.type === 'index',
        'pendo-table__cell--tooltip': showTooltip,
        'pendo-table__cell--tree': column.type === 'tree',
        'pendo-table__cell--checkbox': column.type === 'checkbox',
        'pendo-table__cell--ellipsis': showEllipsis
      }],
      directives: [{
        name: 'pendo-tooltip',
        value: {
          content: tooltipContent,
          delay: {
            show: 300,
            hide: 0
          },
          arrow: true,
          multiline: true,
          html: false,
          boundariesElement: 'body'
        }
      }]
    }, [renderChildren(), renderActions(), renderResizeControl()])]);
  }
};

export { TableHeaderCell as default };
//# sourceMappingURL=table-header-cell.js.map
